import { type CatalogBrandItemDTO, CatalogBrandStatus, type TenantBrandDTO } from '@today/types'
import { FieldShell } from '@today/ui-components'
import { Badge, type BadgeProps, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Props for {@link TenantBrandPanelView}.
 *
 * @property brand - The tenant's resolved brand, or `null` when none is selected (or it left the catalogue).
 * @property editor - Present only when the caller may change the brand (Super-Admin, platform plane).
 */
export interface TenantBrandPanelViewProps {
	brand: TenantBrandDTO | null
	editor?: TenantBrandEditorProps
}

/**
 * The Super-Admin brand picker.
 *
 * @property options - The catalogue; only `selectable` items can be chosen.
 * @property selectedId - The id currently chosen in the picker (`''` = none).
 * @property onSelect - Called with the chosen id.
 * @property onSave - Called when the user saves the chosen id.
 * @property saving - True while a save is in flight; disables Save.
 * @property error - A message to show (e.g. a refused save).
 */
export interface TenantBrandEditorProps {
	options: readonly CatalogBrandItemDTO[]
	selectedId: string
	onSelect: (id: string) => void
	onSave: () => void
	saving: boolean
	error?: string | null
}

const STATUS: Record<CatalogBrandStatus, { label: string; variant: NonNullable<BadgeProps['variant']> }> = {
	[CatalogBrandStatus.PUBLISHED]: { label: 'published', variant: 'success' },
	[CatalogBrandStatus.DEPRECATED]: { label: 'deprecated', variant: 'muted' },
}

const SELECT =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/** The brand's name, status and one swatch per palette colour. */
function BrandPreview({ brand }: { brand: TenantBrandDTO }): ReactElement {
	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-2">
				<span className="text-ds-base font-medium text-ds-text">{brand.name}</span>
				<Badge variant={STATUS[brand.status].variant} size="sm">
					{STATUS[brand.status].label}
				</Badge>
			</div>
			<ul aria-label={`${brand.name} palette`} className="flex flex-wrap gap-2">
				{Object.entries(brand.palette).map(([name, value]) => (
					<li key={name} className="flex flex-col items-center gap-1 text-ds-xs text-ds-text-muted">
						<span
							data-testid="brand-swatch"
							title={`${name}: ${value}`}
							className="block h-8 w-8 rounded-ds-sm border border-ds-border"
							style={{ backgroundColor: value }}
						/>
						<span>{name}</span>
					</li>
				))}
			</ul>
		</div>
	)
}

/** The catalogue picker: deprecated brands are listed but cannot be chosen. */
function BrandEditor({ editor }: { editor: TenantBrandEditorProps }): ReactElement {
	return (
		<div className="flex flex-col gap-3">
			<FieldShell label="Brand" error={editor.error ?? undefined}>
				<select className={SELECT} value={editor.selectedId} onChange={(e) => editor.onSelect(e.target.value)}>
					<option value="" disabled>
						Choose a brand
					</option>
					{editor.options.map((option) => (
						<option key={option.id} value={option.id} disabled={!option.selectable}>
							{option.selectable ? option.name : `${option.name} (${option.status})`}
						</option>
					))}
				</select>
			</FieldShell>
			<div>
				<Button type="button" onPress={editor.onSave} isDisabled={editor.saving || editor.selectedId === ''}>
					Save
				</Button>
			</div>
		</div>
	)
}

/**
 * A tenant's brand (TO-380, ADR-0045): the current brand's preview, and — only
 * when an `editor` is given — the catalogue picker with Save. Pure: the brand,
 * the catalogue and the callbacks are props.
 */
export function TenantBrandPanelView({ brand, editor }: TenantBrandPanelViewProps): ReactElement {
	return (
		<section className="flex flex-col gap-6">
			{brand ? <BrandPreview brand={brand} /> : <p className="text-ds-sm text-ds-text-muted">No brand selected</p>}
			{editor ? <BrandEditor editor={editor} /> : null}
		</section>
	)
}
